import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'src/store';
import FollowUpSettingsForm from '../FollowUpSettingsForm';
import { FollowUpSettings } from 'src/types/followUpSettings';
import {
  updateConversationFollowUp,
  enableConversationFollowUp,
  disableConversationFollowUp
} from 'src/features/account/conversations.slice';
import { 
  Box, 
  Chip, 
  Fade, 
  LinearProgress,
  Typography 
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { isEqual } from 'lodash';

interface ConversationFollowUpSettingsProps {
  conversationId: string;
}

const DEFAULT_PROMPT = "Monitor this conversation and determine if a follow-up is needed. Consider: user engagement, unanswered questions, and potential next steps. If appropriate, draft and send a follow-up message.";

const ConversationFollowUpSettings: React.FC<ConversationFollowUpSettingsProps> = ({ conversationId }) => {
  const dispatch = useAppDispatch();
  const conversation = useAppSelector(state => state.conversations.entities[conversationId]);
  
  // Track the last known remote state to detect actual changes
  const lastKnownRemoteState = useRef<FollowUpSettings | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Derive if any follow-up operation is in progress
  const isFollowUpOperationInProgress = useMemo(() => {
    // Check if conversation is loading or if we're saving
    return conversation?.loading || saving;
  }, [conversation?.loading, saving]);
  
  // Inherit settings from conversation.follow_up or use defaults
  const inheritedSettings = useMemo((): FollowUpSettings => {
    const followUp = conversation?.follow_up || {};
    return {
      enabled: followUp.enabled ?? false,
      delay_minutes: followUp.delay_minutes ?? 1440,
      prompt: followUp.prompt ?? DEFAULT_PROMPT,
      respect_business_hours: followUp.respect_business_hours ?? false,
      max_message_attempts: followUp.max_message_attempts ?? 5,
      schedule_type: followUp.schedule_type ?? 'auto',
      skip_weekends: followUp.skip_weekends ?? false,
      limit_follow_up_attempts: followUp.limit_follow_up_attempts ?? false
    };
  }, [conversation?.follow_up]);

  const [followUpSettings, setFollowUpSettings] = useState<FollowUpSettings>(inheritedSettings);
  const [initialFollowUpSettings, setInitialFollowUpSettings] = useState<FollowUpSettings>(inheritedSettings);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasFormFocus, setHasFormFocus] = useState(false);
  const [showExternalChangeNotification, setShowExternalChangeNotification] = useState(false);
  const [notificationTimer, setNotificationTimer] = useState<NodeJS.Timeout | null>(null);

  // Initialize last known remote state
  useEffect(() => {
    if (!lastKnownRemoteState.current) {
      lastKnownRemoteState.current = inheritedSettings;
    }
  }, []);

  // Track when user has made local changes
  const hasLocalChanges = useMemo(() => {
    return !isEqual(followUpSettings, initialFollowUpSettings);
  }, [followUpSettings, initialFollowUpSettings]);

  // Detect ACTUAL external changes
  useEffect(() => {
    // Skip if this is the first render, form has focus, or operation is in progress
    if (!lastKnownRemoteState.current || hasFormFocus || isFollowUpOperationInProgress) {
      return;
    }

    // Check if remote state has actually changed
    const remoteStateChanged = !isEqual(inheritedSettings, lastKnownRemoteState.current);
    
    if (remoteStateChanged) {
      // Update our tracking of remote state
      lastKnownRemoteState.current = inheritedSettings;
      
      // Auto-update if user hasn't made local changes
      if (!hasLocalChanges) {
        setFollowUpSettings(inheritedSettings);
        setInitialFollowUpSettings(inheritedSettings);
      } else {
        // Show subtle notification for 5 seconds
        setShowExternalChangeNotification(true);
        
        // Clear any existing timer
        if (notificationTimer) {
          clearTimeout(notificationTimer);
        }
        
        // Set new timer to hide notification
        const timer = setTimeout(() => {
          setShowExternalChangeNotification(false);
        }, 5000);
        setNotificationTimer(timer);
      }
    }
  }, [inheritedSettings, hasFormFocus, hasLocalChanges, isFollowUpOperationInProgress]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (notificationTimer) {
        clearTimeout(notificationTimer);
      }
    };
  }, [notificationTimer]);

  // Reset notification when form gains focus or when operations start
  useEffect(() => {
    if ((hasFormFocus && isExpanded) || isFollowUpOperationInProgress) {
      setShowExternalChangeNotification(false);
      if (notificationTimer) {
        clearTimeout(notificationTimer);
      }
      // Update last known remote state when user focuses
      lastKnownRemoteState.current = inheritedSettings;
    }
  }, [hasFormFocus, isExpanded, isFollowUpOperationInProgress, inheritedSettings]);

  // Reset local state when conversation changes
  useEffect(() => {
    setFollowUpSettings(inheritedSettings);
    setInitialFollowUpSettings(inheritedSettings);
    setShowExternalChangeNotification(false);
    // Reset tracking for new conversation
    lastKnownRemoteState.current = inheritedSettings;
  }, [conversationId]); // Only depend on conversationId

  // Handle immediate enable/disable toggle
  const handleToggleEnable = async (enabled: boolean) => {
    try {
      setSaving(true);
      if (enabled) {
        await dispatch(enableConversationFollowUp(conversationId)).unwrap();
      } else {
        await dispatch(disableConversationFollowUp(conversationId)).unwrap();
      }
      // Update local state to match the change
      setFollowUpSettings(prev => ({ ...prev, enabled }));
      setInitialFollowUpSettings(prev => ({ ...prev, enabled }));
      // Update last known remote state
      lastKnownRemoteState.current = { ...lastKnownRemoteState.current!, enabled };
    } catch (error) {
      console.error('Failed to toggle follow-up settings:', error);
      // Revert the UI state if the API call fails
      setFollowUpSettings(prev => ({ ...prev, enabled: !enabled }));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await dispatch(
        updateConversationFollowUp({
          conversationId,
          followUpSettings,
        })
      ).unwrap();

      // Update initialFollowUpSettings to match the current settings after saving
      setInitialFollowUpSettings({ ...followUpSettings });
      // Update last known remote state
      lastKnownRemoteState.current = { ...followUpSettings };
      setIsExpanded(false);
      setShowExternalChangeNotification(false);
    } catch (error) {
      console.error('Failed to update follow-up settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptExternalChanges = () => {
    setFollowUpSettings(inheritedSettings);
    setInitialFollowUpSettings(inheritedSettings);
    setShowExternalChangeNotification(false);
    if (notificationTimer) {
      clearTimeout(notificationTimer);
    }
  };

  const handleDismissNotification = () => {
    setShowExternalChangeNotification(false);
    if (notificationTimer) {
      clearTimeout(notificationTimer);
    }
  };

  return (
    <Box position="relative">
      
      {/* Subtle external change notification */}
      <Fade in={showExternalChangeNotification && !isFollowUpOperationInProgress}>
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 8, 
            right: 8, 
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Chip
            icon={<InfoIcon sx={{ fontSize: 16 }} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                  Updated elsewhere
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    textDecoration: 'underline', 
                    cursor: 'pointer',
                    fontWeight: 600,
                    color: 'primary.main'
                  }}
                  onClick={handleAcceptExternalChanges}
                >
                  Sync
                </Typography>
              </Box>
            }
            size="small"
            color="default"
            variant="outlined"
            onDelete={handleDismissNotification}
            sx={{ 
              height: 24,
              borderStyle: 'dashed',
              backgroundColor: theme => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
              borderColor: theme => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300',
              '& .MuiChip-deleteIcon': {
                fontSize: 14,
                marginLeft: '-2px',
                marginRight: '4px'
              },
              '& .MuiChip-label': {
                paddingLeft: '8px',
                paddingRight: '8px'
              }
            }}
          />
        </Box>
      </Fade>

      {/* Form with disabled state during operations */}
      <Box sx={{ opacity: isFollowUpOperationInProgress ? 0.7 : 1 }}>
        <FollowUpSettingsForm
          followUpSettings={followUpSettings}
          setFollowUpSettings={setFollowUpSettings}
          handleSave={handleSave}
          saving={saving}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          initialFollowUpSettings={initialFollowUpSettings}
          handleToggleEnable={handleToggleEnable}
          onFocusChange={setHasFormFocus}
          disabled={isFollowUpOperationInProgress}
        />
      </Box>
    </Box>
  );
};

export default ConversationFollowUpSettings;
